import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Tone = 'neutral' | 'green' | 'blue' | 'amber' | 'red' | 'zinc';

const tones: Record<Tone, string> = {
  neutral: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  zinc: 'bg-zinc-900 text-white border-zinc-900',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}

export function Badge({ className, tone = 'neutral', dot = false, children, ...rest }: BadgeProps) {
  const dotColor: Record<Tone, string> = {
    neutral: 'bg-zinc-500',
    zinc: 'bg-white',
    green: 'bg-emerald-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 border rounded-full text-[11px] leading-none font-medium tracking-tightish whitespace-nowrap',
        'px-2 py-1',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            dotColor[tone],
            (tone === 'blue' || tone === 'amber') && 'animate-pulse-dot',
          )}
        />
      )}
      {children}
    </span>
  );
}
